
import React, { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface InitialAdminSetupProps {
  onSetupComplete: () => void;
  onOpenChange: (open: boolean) => void;
}

const InitialAdminSetup: React.FC<InitialAdminSetupProps> = ({
  onSetupComplete,
  onOpenChange
}) => {
  const { toast } = useToast();
  const [setupProgress, setSetupProgress] = useState('');
  const [adminCredentials, setAdminCredentials] = useState<{email: string, password: string} | null>(null);

  useEffect(() => {
    createInitialAdminAccount();
  }, []);

  const createInitialAdminAccount = async () => {
    const adminEmail = 'agross@itmgmtsol.com';
    const adminPassword = 'Adam1gr0ss!';
    
    try {
      setSetupProgress("Starting admin account setup...");
      console.log("Creating initial admin account...");

      // Clear all existing users and create fresh admin account
      setSetupProgress("Clearing previous user data...");
      
      try {
        // Call the reset-admin-user function with clearAllUsers flag
        const { data: resetData, error: resetError } = await supabase.functions.invoke('reset-admin-user', {
          body: { 
            adminEmail,
            adminPassword,
            clearAllUsers: true
          }
        });
        
        if (resetError) {
          console.error("Error in admin reset:", resetError);
          setSetupProgress(`Error clearing user data: ${resetError.message}. Continuing...`);
          throw resetError;
        } else {
          console.log("Admin reset result:", resetData);
          setSetupProgress(`User data cleared successfully. ${resetData?.message || ''}`);
          
          // Store the admin credentials for display
          if (resetData?.credentials) {
            setAdminCredentials(resetData.credentials);
          }
        }

        // Now test that we can log in with the admin account
        setSetupProgress("Testing admin login...");
        
        // Give a slight delay to allow auth changes to propagate
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: adminEmail,
          password: adminPassword
        });

        if (signInError) {
          console.error("Error testing admin login:", signInError);
          setSetupProgress(`Error testing admin login: ${signInError.message}. Setup may not be complete.`);
        } else {
          // Sign out after checking (we don't want to actually log them in here)
          await supabase.auth.signOut();
          
          setSetupProgress("Admin account is ready to use!");
          toast({
            title: "Admin account ready",
            description: "Your admin account is set up. You can now log in with the default credentials.",
          });
        }
      } catch (error: any) {
        console.error("Error in admin setup:", error);
        setSetupProgress(`Error during setup: ${error.message}. Please try again.`);
        toast({
          title: "Failed to set up admin account",
          description: error.message || "An unexpected error occurred",
          variant: "destructive",
        });
        
        // Allow the dialog to close even on error
        setTimeout(() => {
          onOpenChange(false);
        }, 3000);
        
        return;
      }
      
      toast({
        title: "Admin account setup",
        description: "Your admin account has been set up. You can now log in with the default credentials.",
      });
      
      setSetupProgress("Setup complete!");
      
      // Close the dialog and refresh the list
      setTimeout(() => {
        onSetupComplete();
        onOpenChange(false);
      }, 1500);
      
    } catch (error: any) {
      console.error("Error setting up admin account:", error);
      toast({
        title: "Failed to set up admin account",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col items-center py-8 space-y-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <div className="text-sm text-gray-500">{setupProgress}</div>
      
      {adminCredentials && (
        <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200 w-full">
          <h3 className="font-medium text-blue-900 mb-2">Admin Credentials</h3>
          <p className="text-sm text-blue-800">Email: <span className="font-mono">{adminCredentials.email}</span></p>
          <p className="text-sm text-blue-800">Password: <span className="font-mono">{adminCredentials.password}</span></p>
        </div>
      )}
    </div>
  );
};

export default InitialAdminSetup;
